import React, {useEffect, useMemo, useState} from 'react';
import Environment from '@ezcater/env-vars';
import type {IAssignmentLogger} from '@eppo/js-client-sdk';
import * as Eppo from '@eppo/js-client-sdk'; // eslint-disable-line import/no-namespace
import {captureException} from '@sentry/browser';
import {Provider as EppoProvider} from '@ezcater/eppo-react';

import LoadingSpinner from '../../components/LoadingSpinner';

const DEFAULT_TRACKING_ID = 'anonymous';

const assignmentLogger: IAssignmentLogger = {
  logAssignment: assignment => {
    console.log('eppo assignment', assignment);
  },
};

type AssignmentsProviderProps = {
  children: React.ReactNode;
  overridesConfiguration?: Record<string, boolean | string | number | Record<string, unknown>>;
  trackingId?: string;
  /**
   * Initialize Eppo in offline mode. This is meant for automated tests.
   */
  useOfflineMode?: boolean;
};

const AssignmentsProvider = ({
  children,
  overridesConfiguration = {},
  trackingId = DEFAULT_TRACKING_ID,
  useOfflineMode = false,
}: AssignmentsProviderProps) => {
  const apiKey = Environment.fetch('EPPO_API_KEY', '');
  const [isInitialized, setIsInitialized] = useState(useOfflineMode);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (useOfflineMode || !apiKey) {
      Eppo.offlineInit({flagsConfiguration: {}});
      setIsInitialized(true);
    } else {
      Eppo.init({apiKey, assignmentLogger, throwOnFailedInitialization: true})
        .catch(e => {
          console.error(e);
          captureException(e);
        })
        .finally(() => setIsInitialized(true));
    }
  }, [apiKey, isInitialized, useOfflineMode]);

  const subjectAttributes = useMemo(() => ({}), []);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <EppoProvider
      overrides={overridesConfiguration}
      subjectAttributes={subjectAttributes}
      subjectKey={trackingId}
    >
      {children}
    </EppoProvider>
  );
};

export default AssignmentsProvider;
